import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Copy, Zap, ExternalLink, RefreshCw, CheckCircle2 } from "lucide-react";

function fmt(d) { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return d; } }

const CopyRow = ({ label, value, mask }) => {
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(!mask);
  const shown = reveal ? value : "•".repeat(Math.max(16, (value || "").length));
  const copy = async () => {
    await navigator.clipboard.writeText(value || "");
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    toast.success(`${label} copied`);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="flex items-center gap-2">
        <Input readOnly value={shown} className="mono text-xs" />
        {mask && <Button variant="outline" size="sm" onClick={()=>setReveal(r=>!r)}>{reveal?"Hide":"Show"}</Button>}
        <Button data-testid={`copy-${label.toLowerCase().replace(/\s/g,"-")}`} size="sm" onClick={copy}>
          {copied ? <CheckCircle2 className="w-4 h-4 mr-1"/> : <Copy className="w-4 h-4 mr-1"/>}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
};

export default function LeadWebhookSetup() {
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => { const r = await api.get("/webhooks/leads/config"); setCfg(r.data); };
  useEffect(() => { load(); }, []);

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/webhooks/leads/test");
      toast.success(`Test lead created: ${r.data.lead_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-slate-500">Loading…</div>;

  const samplePayload = `{
  "secret": "${cfg.webhook_secret}",
  "department": "STUDY",
  "full_name": "John Smith",
  "phone": "+91-99999-00000",
  "email": "john@example.com",
  "source": "Instagram",
  "query_about": "Study Permit",
  "notes": "Inquired via IG DM about SPE"
}`;

  const metaMapping = [
    ["Meta field", "Our field", "Notes"],
    ["full_name (or name)", "full_name", "First+Last will be joined automatically"],
    ["phone_number (or phone, mobile)", "phone", "Required"],
    ["email", "email", "Optional but recommended"],
    ["(hidden field or ad-set mapping)", "department", "REQUIRED — must be exactly STUDY or FILING. Map from a hidden lead-form field, ad-set label, or campaign name."],
    ["form_name / ad_name / campaign_name", "notes (auto-appended)", "Adds context to the lead"],
    ["platform / page_name", "source (auto-detected)", "Instagram / Facebook / WhatsApp"],
    ["service / file_type / query_about", "app_type_id (fuzzy-matched)", "Falls back to \"Other\""],
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900 flex items-center gap-2"><Zap className="w-7 h-7 text-orange-500"/>Meta Lead Ads Sync</h1>
        <p className="text-sm text-slate-500 mt-1">Wire your Instagram / Facebook lead forms to auto-drop into the Leads inbox via Zapier or Make. Zero manual copy-paste. No Meta app review needed.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="heading-font font-semibold">Your webhook credentials</div>
        <CopyRow label="Webhook URL" value={cfg.webhook_url} />
        <CopyRow label="Secret Token" value={cfg.webhook_secret} mask />
        <div className="flex gap-2 pt-2">
          <Button data-testid="webhook-test-btn" onClick={runTest} disabled={testing} className="bg-orange-500 hover:bg-orange-600 text-white">
            <RefreshCw className={`w-4 h-4 mr-1 ${testing?"animate-spin":""}`}/>{testing?"Firing…":"Fire test payload"}
          </Button>
          <Button variant="outline" onClick={load}>Refresh</Button>
        </div>
        <p className="text-xs text-slate-500">The test creates one real lead named "Webhook Test Lead" so you can watch it appear in the Leads inbox. Delete or mark Lost afterwards.</p>
      </div>

      <Tabs defaultValue="zapier">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="zapier">Setup via Zapier</TabsTrigger>
          <TabsTrigger value="make">Setup via Make (Integromat)</TabsTrigger>
          <TabsTrigger value="fields">Field Mapping</TabsTrigger>
          <TabsTrigger value="raw">Raw JSON payload</TabsTrigger>
          <TabsTrigger value="recent">Recent Webhook Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="zapier">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 text-sm">
            <ol className="list-decimal list-inside space-y-2 text-slate-700">
              <li>Go to <a className="text-orange-600 hover:underline inline-flex items-center gap-1" href="https://zapier.com/apps/facebook-lead-ads/integrations" target="_blank" rel="noreferrer">Zapier's Facebook Lead Ads template<ExternalLink className="w-3 h-3"/></a> and click "Try it".</li>
              <li><strong>Trigger:</strong> Facebook Lead Ads → New Lead. Connect your FB account, pick the Page and Form.</li>
              <li><strong>Action:</strong> Webhooks by Zapier → <strong>POST</strong>.</li>
              <li>Paste your Webhook URL from above into the URL field.</li>
              <li>Payload type: <strong>JSON</strong>. Data:
                <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 mono text-xs bg-slate-50 border rounded p-2">
                  <li>secret → <span className="text-slate-500">(your Secret Token)</span></li>
                  <li className="text-rose-700"><strong>department</strong> → <span className="text-slate-500">STUDY or FILING (map from an ad-set label, hidden form field, or the form name — one Zap per team is easiest)</span></li>
                  <li>full_name → <span className="text-slate-500">{'{{'} full_name {'}}'}</span></li>
                  <li>phone → <span className="text-slate-500">{'{{'} phone_number {'}}'}</span></li>
                  <li>email → <span className="text-slate-500">{'{{'} email {'}}'}</span></li>
                  <li>source → <span className="text-slate-500">Facebook (or Instagram if using IG lead form)</span></li>
                  <li>form_name → <span className="text-slate-500">{'{{'} form_name {'}}'}</span></li>
                  <li>ad_name → <span className="text-slate-500">{'{{'} ad_name {'}}'}</span></li>
                  <li>query_about → <span className="text-slate-500">(map to whichever form question captures the visa type — e.g. "What service?")</span></li>
                </ul>
              </li>
              <li>Unwrap arrays: <strong>No</strong>. Wrap Request in Array: <strong>No</strong>.</li>
              <li>Turn on the Zap. Submit a test lead from Meta's <a className="text-orange-600 hover:underline" href="https://developers.facebook.com/tools/lead-ads-testing" target="_blank" rel="noreferrer">Lead Ads Testing Tool</a>.</li>
              <li>Watch it land here → <Link to="/leads" className="text-orange-600 hover:underline">/leads</Link>.</li>
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="make">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 text-sm">
            <ol className="list-decimal list-inside space-y-2 text-slate-700">
              <li>In <a className="text-orange-600 hover:underline" href="https://www.make.com/en/integrations/facebook-lead-ads" target="_blank" rel="noreferrer">Make.com</a> create a new scenario.</li>
              <li><strong>Trigger:</strong> Facebook Lead Ads → "Watch Leads". Pick your Page + Form.</li>
              <li><strong>Action:</strong> HTTP → "Make a request". Method: <strong>POST</strong>. URL: your Webhook URL.</li>
              <li>Headers → add <code className="mono bg-slate-100 px-1 rounded">X-Webhook-Secret</code> = your Secret Token, and <code className="mono bg-slate-100 px-1 rounded">Content-Type</code> = <code className="mono bg-slate-100 px-1 rounded">application/json</code>.</li>
              <li>Body type: <strong>Raw</strong>, Content type: <strong>JSON</strong>. Map fields as shown in the "Field Mapping" tab.</li>
              <li>Run the scenario once and verify a test lead lands.</li>
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="fields">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>{metaMapping[0].map(h=><th key={h} className="text-left px-4 py-2">{h}</th>)}</tr></thead>
              <tbody>
                {metaMapping.slice(1).map((r,i)=>(
                  <tr key={i} className="border-t border-slate-100">
                    {r.map((c,j)=><td key={j} className={`px-4 py-2 ${j===0||j===1?"mono text-xs":""}`}>{c}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
              <div className="font-semibold mb-1">Valid <code className="mono bg-slate-100 px-1 rounded">source</code> values:</div>
              <div className="flex gap-1 flex-wrap">{cfg.sources.map(s=><span key={s} className="px-2 py-0.5 rounded bg-slate-100">{s}</span>)}</div>
              <div className="font-semibold mt-2 mb-1">Application Types (map <code className="mono bg-slate-100 px-1 rounded">query_about</code> to one of these names for exact match):</div>
              <div className="flex gap-1 flex-wrap">{cfg.app_types.map(t=><span key={t.id} className="px-2 py-0.5 rounded bg-slate-100">{t.name}</span>)}</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="raw">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-2">Sample body (curl / Postman):</p>
            <pre className="mono text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">{samplePayload}</pre>
            <div className="text-xs text-slate-500 mt-2">Meta / Zapier's flat <code className="mono bg-slate-100 px-1 rounded">field_data[0][name]</code> pairs are auto-flattened server-side, so plain checkbox-style Zapier data works out of the box.</div>
          </div>
        </TabsContent>

        <TabsContent value="recent">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
                <th className="text-left px-4 py-2">Lead ID</th><th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Source</th><th className="text-left px-4 py-2">Assigned</th>
                <th className="text-left px-4 py-2">Stage</th><th className="text-left px-4 py-2">Received</th>
              </tr></thead>
              <tbody>
                {cfg.recent_leads.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No webhook leads yet. Fire a test above or wire Zapier.</td></tr>}
                {cfg.recent_leads.map(l => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 mono text-xs"><Link to={`/leads/${l.id}`} className="text-orange-600 hover:underline">{l.lead_id}</Link></td>
                    <td className="px-4 py-2 font-medium">{l.full_name}</td>
                    <td className="px-4 py-2">{l.source}</td>
                    <td className="px-4 py-2 text-slate-600">{l.assigned_caller_id ? "✓" : <span className="text-rose-600">Unassigned</span>}</td>
                    <td className="px-4 py-2">{l.stage}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{fmt(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <div className="font-semibold mb-1">🔒 Keep the secret private</div>
        Anyone with the Webhook URL + Secret can push leads into your system. Store the secret in Zapier/Make only. If it leaks, ask your engineer to rotate <code className="mono bg-amber-100 px-1 rounded">LEAD_WEBHOOK_SECRET</code>.
      </div>
    </div>
  );
}
